import React, { useMemo, useState } from "react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import Box from "components/common/Box";
import Text from "components/common/Text";
import IconButton from "components/common/IconButton";
import JarvisButton from "components/JarvisButton";
import { masterApis } from "stores/master/masterApis";
import { dateTimeHelper } from "helper/dateTime";
import { commonActions } from "stores/common/commonActions";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

const SCHEDULE_ERROR =
  "We couldn't schedule your call. Please try again.";

const CALL_DURATION_MINUTES = 60;
const DAYS_PER_WEEK = 7;
const BOOKING_WINDOW_DAYS = DAYS_PER_WEEK * 2;
const SLOT_HOURS = [10, 11, 12, 14, 15, 16, 17, 18, 19];

const SLOT_GROUPS = [
  { label: "Morning", match: (h) => h < 12 },
  { label: "Afternoon", match: (h) => h >= 12 && h < 17 },
  { label: "Evening", match: (h) => h >= 17 },
];

// Dark values follow the PHC report's dark theme (pages/PHC/Dashboard/phcThemes.js).
export const SCHEDULER_PALETTES = {
  light: {
    paper: "#FFFFFF",
    primary: "#2B70C5",
    primarySoft: "#EAF2FF",
    text: "#222B3E",
    muted: "#6B7280",
    border: "#E5E7EB",
    surface: "#F7F9FC",
    disabled: "#C3CAD6",
    success: "#45AC70",
    successSoft: "rgba(69,172,112,0.12)",
    error: "#C40000",
    errorSoft: "#FDECEC",
    shadow: "0px 8px 24px -12px rgba(34,43,62,0.12)",
  },
  dark: {
    paper: "#141414",
    primary: "#2977EC",
    primarySoft: "rgba(41,119,236,0.16)",
    text: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    border: "#393939",
    surface: "#1F1F1F",
    disabled: "rgba(255,255,255,0.28)",
    success: "#45AC70",
    successSoft: "rgba(69,172,112,0.18)",
    error: "#FF5252",
    errorSoft: "rgba(255,82,82,0.12)",
    shadow: "none",
  },
};

const formatHour = (hour) => {
  const h12 = hour % 12 || 12;
  return `${h12}:00 ${hour < 12 ? "am" : "pm"}`;
};

const toApiTime = (hour) => `${String(hour).padStart(2, "0")}:00`;

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isSameDay = (a, b) =>
  !!a && !!b && startOfDay(a).getTime() === startOfDay(b).getTime();

// A slot that has already started today can't be booked.
const isSlotPast = (date, hour, now) =>
  isSameDay(date, now) && hour <= now.getHours();

const getBookableDates = (now) => {
  const today = startOfDay(now);
  const lastHour = SLOT_HOURS[SLOT_HOURS.length - 1];
  const firstOffset = now.getHours() >= lastHour ? 1 : 0;

  return Array.from({ length: BOOKING_WINDOW_DAYS }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + firstOffset + i);
    return d;
  });
};

const getDayLabel = (date, now) => {
  const diff = Math.round(
    (startOfDay(date) - startOfDay(now)) / (24 * 60 * 60 * 1000)
  );
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return date.toLocaleDateString("en-GB", { weekday: "short" });
};

const formatMonthRange = (first, last) => {
  const month = (d) => d.toLocaleDateString("en-GB", { month: "short" });
  const year = last.getFullYear();
  return first.getMonth() === last.getMonth()
    ? `${month(first)} ${year}`
    : `${month(first)} – ${month(last)} ${year}`;
};

const formatLongDate = (date) =>
  date
    ? date.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

const formatShortDate = (date) =>
  date
    ? date.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
      })
    : "";

const SectionLabel = ({ children, right, colors }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 1.25,
    }}
  >
    <Text sx={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
      {children}
    </Text>
    {right}
  </Box>
);

const CallScheduler = ({
  title,
  description,
  onSkip,
  onClose,
  isShowButton,
  leadOrigin,
  variant = "card",
  mode = "light",
}) => {
  const C = SCHEDULER_PALETTES[mode] || SCHEDULER_PALETTES.light;
  const now = useMemo(() => new Date(), []);
  const dates = useMemo(() => getBookableDates(now), [now]);

  const [selectedDate, setSelectedDate] = useState(dates[0]);
  const [selectedHour, setSelectedHour] = useState(null);
  const [weekPage, setWeekPage] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const dispatch = useDispatch();
  const stayOnPage = leadOrigin === "phc";
  const isPlain = variant === "plain";

  const history = useHistory();

  const canSubmit = !!selectedDate && selectedHour !== null && !submitting;
  const weekCount = Math.ceil(dates.length / DAYS_PER_WEEK);
  const weekDates = dates.slice(
    weekPage * DAYS_PER_WEEK,
    (weekPage + 1) * DAYS_PER_WEEK
  );

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setErrorMessage("");
    if (selectedHour !== null && isSlotPast(date, selectedHour, now)) {
      setSelectedHour(null);
    }
  };

  const onSubmit = async () => {
    if (!canSubmit) return;

    const payload = {
      scheduleDate: dateTimeHelper.sipDate(selectedDate),
      scheduleTime: toApiTime(selectedHour),
      ...(leadOrigin ? { leadOrigin } : {}),
    };

    try {
      setSubmitting(true);
      setErrorMessage("");

      await masterApis.submitScheduleCall(payload);

      if (stayOnPage) {
        setSubmitted(true);
        return;
      }

      history.push("/");
    } catch (error) {
      console.error("Schedule error:", error);
      if (stayOnPage) {
        setErrorMessage(SCHEDULE_ERROR);
        return;
      }
      dispatch(
        commonActions.setToast({
          message: "Unable to schedule call. Try again.",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const rootSx = isPlain
    ? {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        background: C.paper,
      }
    : {
        background: C.paper,
        borderRadius: "16px",
        border: `1px solid ${C.border}`,
        boxShadow: C.shadow,
        mt: 2.5,
        overflow: "hidden",
      };

  const padX = { xs: 2.5, sm: 3 };

  const primaryButtonSx = {
    width: "100%",
    height: 48,
    borderRadius: "12px",
    fontWeight: 600,
    background: `${C.primary} !important`,
    color: "#FFF !important",
  };

  if (submitted) {
    const endHour = selectedHour + CALL_DURATION_MINUTES / 60;

    return (
      <Box sx={rootSx}>
        <Box
          sx={{
            px: padX,
            pt: 4,
            pb: 3,
            textAlign: "center",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: C.success,
              boxShadow: `0 0 0 10px ${C.successSoft}`,
              m: "10px auto 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "callSchedulerPop 420ms cubic-bezier(.2,1.4,.4,1)",
              "@keyframes callSchedulerPop": {
                "0%": { transform: "scale(0.4)", opacity: 0 },
                "100%": { transform: "scale(1)", opacity: 1 },
              },
            }}
          >
            <CheckRoundedIcon sx={{ color: "#FFF", fontSize: 40 }} />
          </Box>

          <Text sx={{ fontSize: 20, fontWeight: 700, color: C.text }}>
            Your call is booked!
          </Text>
          <Text
            sx={{
              fontSize: 14,
              color: C.muted,
              mt: 1,
              lineHeight: 1.5,
              maxWidth: 320,
              mx: "auto",
            }}
          >
            Our Relationship Manager will call you at the time you picked.
          </Text>

          <Box
            sx={{
              mt: 3,
              borderRadius: "12px",
              background: C.surface,
              border: `1px solid ${C.border}`,
              textAlign: "left",
            }}
          >
            {[
              {
                icon: CalendarMonthOutlinedIcon,
                label: "Date",
                value: formatLongDate(selectedDate),
              },
              {
                icon: ScheduleOutlinedIcon,
                label: "Time",
                value: `${formatHour(selectedHour)} – ${formatHour(
                  endHour
                )} (${CALL_DURATION_MINUTES} min)`,
              },
            ].map(({ icon: Icon, label, value }, i) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  borderTop: i ? `1px solid ${C.border}` : "none",
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    background: C.primarySoft,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ color: C.primary, fontSize: 20 }} />
                </Box>
                <Box>
                  <Text sx={{ fontSize: 12, color: C.muted }}>{label}</Text>
                  <Text sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                    {value}
                  </Text>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ px: padX, pb: 3 }}>
          <JarvisButton
            disableStartIcon
            disableEndIcon
            sx={primaryButtonSx}
            onClick={onClose || onSkip}
          >
            Done
          </JarvisButton>
        </Box>
      </Box>
    );
  }

  const renderHeader = () => {
    if (!title && !onClose) return null;

    return (
      <>
        {isPlain ? (
          // Bottom-sheet drag handle, mobile only
          <Box
            sx={{
              display: { xs: "block", sm: "none" },
              width: 40,
              height: 4,
              borderRadius: "2px",
              background: C.border,
              m: "10px auto 0",
            }}
          />
        ) : null}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            px: padX,
            pt: { xs: isPlain ? 1.5 : 2.5, sm: 2.5 },
            pb: 2,
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: C.primarySoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <SupportAgentOutlinedIcon sx={{ color: C.primary }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Text sx={{ fontSize: 18, fontWeight: 700, color: C.text }}>
              {title}
            </Text>
            {description ? (
              <Text
                sx={{
                  fontSize: 13,
                  color: C.muted,
                  mt: 0.5,
                  lineHeight: 1.5,
                }}
              >
                {description}
              </Text>
            ) : null}
          </Box>
          {onClose ? (
            <IconButton
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
              sx={{ mt: -0.5, mr: -1, color: C.muted }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </Box>
      </>
    );
  };

  const renderDateChip = (date, sx = {}) => {
    const isSelected = isSameDay(date, selectedDate);
    const subColor = isSelected ? "rgba(255,255,255,0.85)" : C.muted;

    return (
      <Box
        key={date.getTime()}
        component="button"
        type="button"
        disabled={submitting}
        onClick={() => handleDateSelect(date)}
        sx={{
          py: 1.25,
          px: 0,
          borderRadius: "12px",
          border: `1.5px solid ${isSelected ? C.primary : C.border}`,
          background: isSelected ? C.primary : C.paper,
          color: isSelected ? "#FFF" : C.text,
          fontFamily: "inherit",
          cursor: submitting ? "default" : "pointer",
          transition: "all 150ms ease",
          "&:hover:not(:disabled)": isSelected
            ? {}
            : { borderColor: C.primary },
          ...sx,
        }}
      >
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 500,
            color: subColor,
            whiteSpace: "nowrap",
          }}
        >
          {getDayLabel(date, now)}
        </Box>
        <Box sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.4 }}>
          {date.getDate()}
        </Box>
        <Box sx={{ fontSize: 11, color: subColor }}>
          {date.toLocaleDateString("en-GB", { month: "short" })}
        </Box>
      </Box>
    );
  };

  const renderWeekArrow = (direction) => {
    const isPrev = direction === "prev";
    const disabled = isPrev ? weekPage === 0 : weekPage >= weekCount - 1;
    const Icon = isPrev ? ChevronLeftRoundedIcon : ChevronRightRoundedIcon;

    return (
      <IconButton
        size="small"
        aria-label={isPrev ? "Previous week" : "Next week"}
        disabled={disabled}
        onClick={() => setWeekPage((p) => p + (isPrev ? -1 : 1))}
        sx={{
          border: `1px solid ${C.border}`,
          borderRadius: "8px",
          width: 28,
          height: 28,
          color: C.text,
          "&.Mui-disabled": { color: C.disabled, opacity: 0.6 },
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </IconButton>
    );
  };

  const renderSlot = (hour) => {
    const isPast = isSlotPast(selectedDate, hour, now);
    const isSelected = selectedHour === hour;
    const isDisabled = isPast || submitting;

    return (
      <Box
        key={hour}
        component="button"
        type="button"
        disabled={isDisabled}
        onClick={() => {
          setSelectedHour(hour);
          setErrorMessage("");
        }}
        sx={{
          height: 44,
          borderRadius: "10px",
          border: `1.5px solid ${
            isSelected ? C.primary : isPast ? C.surface : C.border
          }`,
          background: isSelected ? C.primary : isPast ? C.surface : C.paper,
          color: isSelected ? "#FFF" : isPast ? C.disabled : C.text,
          fontSize: 14,
          fontWeight: isSelected ? 600 : 500,
          fontFamily: "inherit",
          cursor: isDisabled ? "default" : "pointer",
          transition: "all 150ms ease",
          "&:hover:not(:disabled)": isSelected
            ? {}
            : { borderColor: C.primary, background: C.primarySoft },
        }}
      >
        {formatHour(hour)}
      </Box>
    );
  };

  return (
    <Box sx={rootSx}>
      {renderHeader()}

      <Box
        sx={{
          px: padX,
          pt: 2.5,
          pb: 1,
          ...(isPlain ? { flex: 1, minHeight: 0, overflowY: "auto" } : {}),
        }}
      >
        {/* DATE SELECTION */}
        <SectionLabel
          colors={C}
          right={
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 1,
              }}
            >
              <Text sx={{ fontSize: 13, color: C.muted, mr: 0.5 }}>
                {formatMonthRange(
                  weekDates[0],
                  weekDates[weekDates.length - 1]
                )}
              </Text>
              {renderWeekArrow("prev")}
              {renderWeekArrow("next")}
            </Box>
          }
        >
          Pick a date
        </SectionLabel>

        {/* Mobile: swipeable strip */}
        <Box
          sx={{
            display: { xs: "flex", sm: "none" },
            gap: 1,
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            scrollPaddingInline: "20px",
            mx: -2.5,
            px: 2.5,
            pb: 0.5,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {dates.map((date) =>
            renderDateChip(date, {
              flex: "0 0 auto",
              width: 64,
              scrollSnapAlign: "start",
            })
          )}
        </Box>

        {/* Desktop: one week at a time */}
        <Box
          sx={{
            display: { xs: "none", sm: "grid" },
            gridTemplateColumns: `repeat(${DAYS_PER_WEEK}, 1fr)`,
            gap: 1,
          }}
        >
          {weekDates.map((date) => renderDateChip(date))}
        </Box>

        {/* TIME SLOT SELECTION */}
        <Box sx={{ mt: 3 }}>
          <SectionLabel
            colors={C}
            right={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: 12,
                  color: C.muted,
                  background: C.surface,
                  borderRadius: "999px",
                  px: 1.25,
                  py: 0.5,
                }}
              >
                <ScheduleOutlinedIcon sx={{ fontSize: 14 }} />
                {CALL_DURATION_MINUTES} min call
              </Box>
            }
          >
            Pick a time
          </SectionLabel>

          {SLOT_GROUPS.map((group) => {
            const hours = SLOT_HOURS.filter(group.match);
            return (
              <Box key={group.label} sx={{ mb: 2 }}>
                <Text
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: C.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    mb: 1,
                  }}
                >
                  {group.label}
                </Text>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 1,
                  }}
                >
                  {hours.map(renderSlot)}
                </Box>
              </Box>
            );
          })}
        </Box>

        {errorMessage ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1,
              mb: 1,
              px: 1.5,
              py: 1.25,
              borderRadius: "10px",
              background: C.errorSoft,
              color: C.error,
              fontSize: 13,
            }}
          >
            <ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} />
            {errorMessage}
          </Box>
        ) : null}
      </Box>

      {/* SUBMIT */}
      <Box
        sx={{
          px: padX,
          pt: 2,
          pb: 2.5,
          borderTop: isPlain ? `1px solid ${C.border}` : "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            mb: 1.5,
            fontSize: 13,
            color: canSubmit ? C.text : C.muted,
            fontWeight: canSubmit ? 600 : 400,
          }}
        >
          <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />
          {selectedHour !== null
            ? `${formatShortDate(selectedDate)} · ${formatHour(selectedHour)}`
            : "Select a time slot to continue"}
        </Box>

        <JarvisButton
          disableStartIcon
          disableEndIcon
          sx={{ ...primaryButtonSx, opacity: canSubmit ? 1 : 0.4 }}
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          {submitting ? "Scheduling…" : "Schedule call"}
        </JarvisButton>

        {/* SKIP */}
        {isShowButton && (
          <JarvisButton
            variant="text"
            disableEndIcon
            sx={{
              color: `${C.muted} !important`,
              marginTop: "8px",
              width: "100%",
            }}
            onClick={onSkip}
          >
            Skip this time
          </JarvisButton>
        )}
      </Box>
    </Box>
  );
};

export default CallScheduler;
